import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';

const IMAGE_CDN_URL = 'https://img-cdn.misfitsmarket.com/';

/**
 * An image input that handles image file selection and preview.
 *
 * @param {Object} props
 * @param {boolean} [props.disabled] - Whether the component is disabled
 * @param {string} props.error - Error message to display
 * @param {string} props.id - Unique identifier for the input element
 * @param {string} [props.imageUrl] - URL of an existing image
 * @param {string} [props.label] - Label text for the image input (defaults to "Image")
 * @param {Function} props.onClearImage - Callback function to handle clearing the selected image
 * @param {Function} props.onImageUpload - Callback function that receives (file) when an image is selected
 * @returns {JSX.Element} A form input for image selection with preview capabilities
 */
function ImageInput({ disabled, error, id, imageUrl, label = 'Image', onClearImage, onImageUpload }) {
    const handleImageUpload = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => onImageUpload(file);
        reader.readAsDataURL(file);
    };

    // Images come back from the BE with the CDN URL, but user-uploaded files do not include it.
    // This ensures that all images are displayed using the CDN URL.
    const imgSrc = imageUrl ? `${IMAGE_CDN_URL}${imageUrl.replace(IMAGE_CDN_URL, '')}` : '';

    return (
        <div className="flex flex-col justify-between gap-2">
            <Label htmlFor={id} className="gap-0">
                {label}
            </Label>
            <div className="flex flex-col gap-2">
                {imageUrl && <img className="self-start max-w-80 max-h-40 object-contain" alt="" src={imgSrc} />}
                <div className="flex gap-2">
                    <div className="flex-1 flex gap-2">
                        <Input
                            disabled
                            type="text"
                            value={imageUrl?.split('/').pop() || 'No file selected'}
                            className="opacity-100!"
                            onClick={() => document.getElementById(id).click()}
                        />
                        <input type="file" id={id} accept="image/*" onChange={handleImageUpload} className="hidden" />
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => document.getElementById(id).click()}
                            className="font-normal"
                            disabled={disabled}
                        >
                            Browse
                        </Button>
                    </div>
                    {imageUrl && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClearImage}
                            className="font-normal"
                            disabled={disabled}
                        >
                            Clear
                        </Button>
                    )}
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
        </div>
    );
}

export default ImageInput;
